import { BookmarkXIcon, StarIcon, Trash2Icon, UsersIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTablePresets } from '@/entities/settings';
import { SharePresetDialog } from '@/entities/shared-presets';
import { cn } from '@/shared/lib';
import { ConfirmationButton, IconButton } from '@/shared/ui';
import { PresetRenamePopover } from './PresetRenamePopover';

type Props = {
  /**
   * Ключ рабочей записи в `settings.tables`: имя таблицы (например, `'dialogsListV2'`) либо
   * ключ раздела — у таблицы с разделами активный пресет и вид свои у каждого из них.
   */
  sectionKey: string;
  /**
   * Пользователь снял активный пресет. Задан — снятие берёт на себя вызывающая сторона:
   * у пресета может быть собственный раздел со своим адресом, и куда после снятия ведёт список,
   * решается снаружи.
   */
  onPresetSelect?: (presetId: string | null) => void;
  /**
   * Снятие активного пресета предлагается кнопкой в этом ряду. Задают `false` там, где снять
   * пресет нельзя: он и есть открытый раздел, и снятие оставило бы кнопку без действия.
   */
  canClearActive?: boolean;
};

/**
 * Ряд действий над применённым пресетом рядом с кнопкой «Пресеты»: переименовать, закрепить
 * в меню, сделать системным, поделиться, удалить, снять активный. Пресет не выбран — ряда нет.
 *
 * Действие, которого пользователь над этим пресетом выполнить не вправе, в ряду не показано:
 * у системного пресета без разрешения на управление остаётся только снятие. Получатель
 * совместного пресета закрепляет его у себя и убирает из своего списка, но не правит.
 */
export const PresetActions: FC<Props> = ({ sectionKey, onPresetSelect, canClearActive = true }) => {
  const { t } = useTranslation('tablePresets');
  const { activePreset, canManageSystemPresets, isSharing, rename, setPinned, makeSystem, share, remove, clearActive } =
    useTablePresets(sectionKey);

  const handleClearActive = useCallback(() => {
    if (onPresetSelect) {
      onPresetSelect(null);
      return;
    }
    clearActive();
  }, [clearActive, onPresetSelect]);

  if (!activePreset) return null;

  const pinLabel = activePreset.pinned ? t('unpinFromMenu') : t('pinToMenu');
  const isPersonal = !activePreset.isSystem && !activePreset.isShared;
  const canMakeSystem = isPersonal && canManageSystemPresets;
  const canShare = isPersonal || (activePreset.isShared && activePreset.isEditable);
  const canPinAndRemove = activePreset.isEditable || activePreset.isShared;
  const isRemovedForSelfOnly = activePreset.isShared && !activePreset.isEditable;

  return (
    <div className="flex items-center shrink-0">
      {activePreset.isEditable && (
        <PresetRenamePopover name={activePreset.name} onRename={(name) => rename(activePreset.id, name)} />
      )}
      {canPinAndRemove && (
        <IconButton
          size="icon"
          className="shrink-0"
          label={pinLabel}
          aria-pressed={activePreset.pinned}
          icon={<StarIcon className={cn('size-4', activePreset.pinned && 'fill-current')} />}
          onClick={() => setPinned(activePreset.id, !activePreset.pinned)}
        />
      )}
      {canMakeSystem && (
        <IconButton
          size="icon"
          className="shrink-0"
          label={t('makeSystem')}
          icon={<UsersIcon className="size-4" />}
          onClick={() => makeSystem(activePreset.id)}
        />
      )}
      {canShare && (
        <SharePresetDialog
          title={t('shareTitle', { name: activePreset.name })}
          triggerLabel={t('share')}
          recipients={activePreset.isShared ? activePreset.recipients : []}
          onShare={(recipientIds, onShared) => share(activePreset.id, recipientIds, onShared)}
          isSharing={isSharing}
        />
      )}
      {canPinAndRemove && (
        <ConfirmationButton
          className="shrink-0"
          label={isRemovedForSelfOnly ? t('removeShared') : t('deletePreset')}
          title={
            isRemovedForSelfOnly
              ? t('removeSharedConfirmTitle', { name: activePreset.name })
              : t('deleteConfirmTitle', { name: activePreset.name })
          }
          description={isRemovedForSelfOnly ? t('removeSharedConfirmDescription') : t('deleteConfirmDescription')}
          onConfirm={() => remove(activePreset.id)}
          trigger={{ size: 'icon', icon: <Trash2Icon className="size-4" /> }}
        />
      )}
      {canClearActive && (
        <IconButton
          size="icon"
          className="shrink-0"
          label={t('clearActive')}
          icon={<BookmarkXIcon className="size-4" />}
          onClick={handleClearActive}
        />
      )}
    </div>
  );
};
